import type { TradingSignal } from '../../types'
import type { ExpiryState } from '../../utils/signalExpiry'
import type { ExitReason } from '../../utils/signalPnl'
import { EXIT_REASON_LABEL } from '../../utils/signalPnl'

export const isOpenSignal = (signal: TradingSignal) => signal.status === 'active' || signal.status === 'pending'

export const DirectionChip = ({ direction }: { direction: TradingSignal['signal_type'] }) => (
  <span
    className={`rounded px-1.5 py-0.5 text-[11px] font-bold tracking-wide ${
      direction === 'long' ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'
    }`}
  >
    {direction.toUpperCase()}
  </span>
)

// The light classes are mapped onto the terminal palette by theme/terminal-theme.css.
export const EXPIRY_STYLES: Record<ExpiryState, string> = {
  ok: 'bg-gray-50 text-gray-600',
  soon: 'bg-amber-50 text-amber-800 ring-1 ring-amber-300',
  expired: 'bg-danger-50 text-danger-700',
}

const EXIT_STYLES: Record<ExitReason, string> = {
  target: 'bg-green-500/15 text-green-300',
  stop: 'bg-red-500/15 text-red-300',
  expired: 'bg-gray-500/20 text-gray-300',
  cancelled: 'bg-gray-500/20 text-gray-400',
  closed: 'bg-gray-500/20 text-gray-300',
}

export const ExitReasonChip = ({ reason }: { reason: ExitReason }) => (
  <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${EXIT_STYLES[reason]}`} data-testid="exit-reason">
    {EXIT_REASON_LABEL[reason]}
  </span>
)

/** A closed signal kept in the list but excluded from the track record. */
export const ExcludedChip = ({ note }: { note?: string | null }) => (
  <span
    className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-300 ring-1 ring-amber-500/30"
    title={note ?? 'Excluded from the track record'}
    data-testid="excluded-chip"
  >
    Excluded
  </span>
)

/** "▲ +2.10%" style P&L text colour, by sign. */
export const pnlText = (pnl: number | null) =>
  pnl == null || pnl === 0 ? 'text-gray-300' : pnl > 0 ? 'text-green-400' : 'text-red-400'
